// @givmo/passkeys — the authenticator QR, drawn in the browser (AS-WEB14; D-AS-22).
//
// `qr@0.7.0` (Paul Miller; MIT OR Apache-2.0; zero runtime dependencies), exact-pinned in this
// package and approved by Jonathan as the ONE new dependency of SEC2-AUTHWEB (D-AS-22): it sees the
// TOTP seed, so it runs here, offline, and nothing about the code leaves the page. Every upgrade is
// read before it merges.
//
// The library's `raw` output (the module matrix, quiet zone included) becomes ONE React `<path>`,
// never an SVG string through `dangerouslySetInnerHTML`: the seed's URI is the input, and no markup
// built from it is ever parsed by the browser. `crispEdges` keeps the modules square at any scale.

import { useMemo } from 'react';
import { encodeQR } from 'qr';
import { testIdProp } from '@givmo/design-system';

/** One `M x y h w v 1 h -w z` run per row of dark modules. */
export function qrPath(matrix: readonly (readonly boolean[])[]): string {
  const parts: string[] = [];
  matrix.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (!row[x]) {
        x += 1;
        continue;
      }
      const start = x;
      while (x < row.length && row[x]) x += 1;
      parts.push(`M${start} ${y}h${x - start}v1h-${x - start}z`);
    }
  });
  return parts.join('');
}

export interface AuthenticatorQrProps {
  /** The `otpauth://` URI. A live secret: rendered as modules, never as text. */
  value: string;
  /** The accessible name: what scanning it does. */
  label: string;
  /** The drawn size in px. */
  size?: number;
  testId?: string;
}

export function AuthenticatorQr({ value, label, size = 148, testId }: AuthenticatorQrProps) {
  const matrix = useMemo(() => encodeQR(value, 'raw', { ecc: 'medium' }), [value]);
  const path = useMemo(() => qrPath(matrix), [matrix]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${matrix.length} ${matrix.length}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="block bg-white"
      {...testIdProp(testId)}
    >
      <path d={path} fill="#000" />
    </svg>
  );
}
